// Centralised mapping from any axios/login error into a clear, user-facing
// message. Used by all three login pages so the UX is consistent.
//
// Why this exists: previously each page did `err.response?.data?.message ||
// err.message || 'Login failed'` which left huge gaps — server down would
// show "Network Error", a timeout would show the raw axios string, and the
// validator-array 400 response from express-validator showed nothing.

export type LoginErrorField = 'email' | 'password' | null;

export interface LoginErrorInfo {
  message: string;
  field: LoginErrorField; // which form field (if any) to visually flag
}

interface ValidationItem {
  param?: string;
  path?: string;
  msg?: string;
}

function pickValidationMessage(errors: ValidationItem[] | undefined): string | null {
  if (!errors || errors.length === 0) return null;
  const first = errors[0];
  const field = (first.param || first.path || '').toLowerCase();
  const msg = first.msg && first.msg !== 'Invalid value' ? first.msg : null;

  if (field === 'email') {
    return msg || 'Please enter a valid email address.';
  }
  if (field === 'password') {
    return msg || 'Password must be at least 6 characters.';
  }
  return msg || 'Please check the highlighted fields.';
}

function fieldFromBackendError(error: string | undefined): LoginErrorField {
  if (!error) return null;
  const lower = error.toLowerCase();
  if (lower.includes('email')) return 'email';
  if (lower.includes('password')) return 'password';
  return null;
}

export function getLoginErrorMessage(err: unknown): LoginErrorInfo {
  // No `response` from the server means we never got a HTTP reply — server
  // is unreachable, DNS failed, CORS blocked, network down, etc.
  const anyErr = err as any;

  if (anyErr?.code === 'ECONNABORTED' || /timeout/i.test(String(anyErr?.message))) {
    return {
      message: 'The server is taking too long to respond. Please try again.',
      field: null,
    };
  }

  if (anyErr?.message === 'Network Error' || (!anyErr?.response && anyErr?.request)) {
    return {
      message:
        'Cannot reach the server. Please check your internet connection and try again.',
      field: null,
    };
  }

  const status: number | undefined = anyErr?.response?.status;
  const data = anyErr?.response?.data;
  const backendMessage: string | undefined = data?.message;
  const backendError: string | undefined = data?.error;

  switch (status) {
    case 400: {
      const validationMsg = pickValidationMessage(data?.errors);
      const field = data?.errors?.[0]?.param || data?.errors?.[0]?.path;
      return {
        message: validationMsg || backendMessage || backendError || 'Please check your input.',
        field: field === 'email' || field === 'password' ? field : null,
      };
    }

    case 401:
      return {
        message: backendMessage || backendError || 'Invalid email or password.',
        field: fieldFromBackendError(backendError),
      };

    case 403:
      // Wrong portal / role used to return a specific 403 message that leaked
      // which portal the account belongs to. Always show the same generic text
      // as a wrong password (covers stale API processes still on old code).
      return {
        message: 'Invalid email or password.',
        field: null,
      };

    case 404:
      return {
        message: backendMessage || backendError || 'Account not found.',
        field: 'email',
      };

    case 423:
      return {
        message: backendMessage || backendError || 'This account is locked. Please contact the administrator.',
        field: null,
      };

    case 429:
      return {
        message:
          backendMessage ||
          'Too many login attempts. Please wait a moment and try again.',
        field: null,
      };

    case 500:
    case 501:
    case 502:
    case 503:
    case 504:
      return {
        message: 'The server is temporarily unavailable. Please try again in a moment.',
        field: null,
      };

    default:
      return {
        message: backendMessage || backendError || 'Login failed. Please try again.',
        field: null,
      };
  }
}

export function get2FAErrorMessage(err: unknown): string {
  const anyErr = err as any;

  if (anyErr?.code === 'ECONNABORTED' || /timeout/i.test(String(anyErr?.message))) {
    return 'The server is taking too long to respond. Please try again.';
  }

  if (anyErr?.message === 'Network Error' || (!anyErr?.response && anyErr?.request)) {
    return 'Cannot reach the server. Please check your internet connection and try again.';
  }

  const status: number | undefined = anyErr?.response?.status;
  const data = anyErr?.response?.data;

  if (status === 401) {
    return data?.message || 'The verification code is incorrect or has expired.';
  }
  if (status && status >= 500) {
    return 'The server is temporarily unavailable. Please try again in a moment.';
  }

  return data?.message || data?.error || 'Verification failed. Please try again.';
}
